/** @jsx isolateComponent */

import { useState } from 'react';
import { css } from '@emotion/core';
import { isolateComponent } from '@zapier/style-encapsulation';

import { Icon } from '@zapier/zinnia-icons';
import { Animation, Colors, Shadows, Typography } from '@zapier/design-system';

import { createKeyHandler } from '../../../utils';

type IconName = any; // TODO: figure out https://zapier.slack.com/archives/C0466L7SX/p1584045313008500
type RadioInputProps = {
  /** Optional name of an icon to render before the label */
  icon?: IconName;
  /** Label */
  label: string;
  /** Function to call on click */
  onClick: () => void;
};

const Styles = {
  root: (hasIcon: boolean) => css`
    ${Typography.SmallPrint3}
    transition-property: box-shadow, border-color, background-color, color;
    transition-timing-function: ${Animation.transitionTimingFunction};
    transition-duration: ${Animation.transitionDuration};
    position: relative;
    cursor: pointer;
    height: 50px;
    border: 1px solid ${Colors.GrayWarm2};
    border-radius: 5px;
    background-color: ${Colors.GrayWarm2};
    padding-left: 13px;
    padding-right: 13px;
    display: grid;
    grid-column-gap: 13px;
    grid-template-columns: ${hasIcon
      ? 'min-content 1fr min-content'
      : '1fr min-content'};
    align-items: center;
    // This is the outline that renders around the button when
    // it's focused. It's a pseudo element to preserve transparency
    // between the edge of the button and the edge of the outline.
    // It's defined here vs only in ':focus' so that it can transition
    // between states.
    &::before {
      content: '';
      position: absolute;
      pointer-events: none;
      top: 0px;
      right: 0px;
      bottom: 0px;
      left: 0px;
      border: 3px solid transparent;
      border-radius: 9px;
      transition-property: all;
      transition-timing-function: ${Animation.transitionTimingFunction};
      transition-duration: ${Animation.transitionDuration};
    }
    &:hover,
    &:focus {
      color: ${Colors.UiPrimaryStrongest};
      fill: ${Colors.UiPrimaryStrongest};
      box-shadow: ${Shadows.elevation10};
      border-color: ${Colors.GrayWarm4};
      background-color: ${Colors.PrimeWhite};
    }
    &:focus::before {
      border-color: ${Colors.UiPrimaryStrongest};
      top: -6px;
      right: -6px;
      bottom: -6px;
      left: -6px;
    }
  `,
  label: css`
    color: inherit;
    whitespace: nowrap;
    overflow: hidden;
    textoverflow: ellipsis;
  `,
};

/**
 * Renders a single item for the `RadioBlock`.
 */
const RadioInput = ({ icon, label, onClick }: RadioInputProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      css={Styles.root(!!icon)}
      onClick={onClick}
      onKeyDown={createKeyHandler(['enter'], onClick)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="button"
      tabIndex={0}
    >
      {icon && (
        <Icon
          color={isHovered ? 'UiPrimaryStrongest' : 'PrimeBlack'}
          name={icon}
          size={20}
        />
      )}
      <label css={Styles.label}>{label}</label>
      <Icon
        color={isHovered ? 'UiPrimaryStrongest' : 'GrayWarm6'}
        name="arrowSmallRight"
        size={20}
      />
    </div>
  );
};

export default RadioInput;
